import type { Student } from '~/@entities/Student'
import type {
  GetChallengeSettingData,
  GetCurriculumAnalysisProblemWeeklySummaryByStudentData,
  GetCurriculumAnalysisProblemWeeklySummaryByStudentParams,
  GetLittleChapterInformationOnRecentlyChallengeByStudentIdData,
  GetMaterialViewByStudentId1Data,
  GetMaterialViewByStudentId1Params,
  GetMaterialViewByStudentIdData,
  GetRecentlyCurriculumsData,
  GetStrengthsAndWeaknessesConceptChipByStudentIdAndCurriculumKeyData,
  GetStrengthsAndWeaknessesConceptChipByStudentIdAndCurriculumKeyParams,
  GetWeeklyInformationByStudentIdData,
  GetWeeklyInformationByStudentIdParams,
  MaterialViewPageable,
  SendOpinionData,
  StudentHomeOpinionSendingRequest,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'

import {
  adaptAssignedChallenge,
  adaptRecentChallenge,
  adaptRecentChallengeCurriculum,
  adaptRecentStudies,
  adaptRecentStudyItems,
  adaptStrengthsAndWeaknesses,
  adaptWeeklyInfo,
  adaptWeeklyLearningSummaries,
} from './adapters'
import type {
  AssignedChallenge,
  RecentChallenge,
  RecentChallengeCurriculum,
  RecentStudies,
  RecentStudyItem,
  StrengthsAndWeaknesses,
  WeeklyInfo,
  WeeklyLearningSummary,
} from './schemas'

// 로그인한 학생 기준으로 답하는 /student-home/* 와, 학생 id 를 받아 학생·학부모 홈이 같이 쓰는 요약 둘이다.
// 챌린지 설정·최근 챌린지 교육과정 두 응답만 CommonResponse 봉투 없이 온다.

/** 이번 주 학습 요약. date(yyyy-MM-dd) 를 주면 그 날이 속한 주다 */
export const getWeeklyInfo = async (
  date?: GetWeeklyInformationByStudentIdParams['date'],
  meta?: RequestMeta,
): Promise<WeeklyInfo> => {
  const body = await httpClient().get<GetWeeklyInformationByStudentIdData>('/student-home/weekly', {
    params: { date },
    meta,
  })
  return adaptWeeklyInfo(body.data)
}

// 커서는 직전 응답의 pageable 을 그대로 다시 싣는다. 서버는 비어 있는 커서를 nullable 로 받고, 스펙은
// CAS 커서 lastAssignedCasId 를 빠뜨렸다. 2026-10-16 백엔드 develop(b1add2a8c) 의 MaterialViewPageable.kt 로 확인.
export type RecentStudiesParams = WithNullableOptionals<MaterialViewPageable> & {
  lastAssignedCasId?: number | null
}

/** 최근 학습(학습지·교재·숙제·경시대회·반응형평가)을 종류 거름과 커서로 받는다 */
export const getRecentStudies = async (
  params: RecentStudiesParams,
  meta?: RequestMeta,
): Promise<RecentStudies> => {
  const body = await httpClient().get<GetMaterialViewByStudentIdData>(
    '/student-home/materialized-view',
    { params, meta },
  )
  return adaptRecentStudies(body.data)
}

/** 최근 챌린지한 단원들과 단원마다의 유형칩 성취도. 챌린지 이력이 없으면 null */
export const getRecentChallenge = async (meta?: RequestMeta): Promise<RecentChallenge | null> => {
  const body =
    await httpClient().get<GetLittleChapterInformationOnRecentlyChallengeByStudentIdData>(
      '/student-home/recently-challenge',
      { meta },
    )
  return adaptRecentChallenge(body.data)
}

export type StudentHomeOpinionBody = StudentHomeOpinionSendingRequest

/** 학습 홈의 의견 보내기 */
export const sendStudentHomeOpinion = async (
  body: StudentHomeOpinionBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<SendOpinionData>('/student-home/opinion', body, { meta })
}

/** 선생님이 걸어 둔 스마일 챌린지 단원 */
export const getAssignedChallenge = async (meta?: RequestMeta): Promise<AssignedChallenge> =>
  adaptAssignedChallenge(
    await httpClient().get<GetChallengeSettingData>('/student-home/smile-challenges', { meta }),
  )

/** 최근 챌린지한 교육과정과 그 단원들의 유형칩 */
export const getRecentChallengeCurriculum = async (
  meta?: RequestMeta,
): Promise<RecentChallengeCurriculum> =>
  adaptRecentChallengeCurriculum(
    await httpClient().get<GetRecentlyCurriculumsData>(
      '/student-home/recently-challenge-curriculums',
      { meta },
    ),
  )

/** 학생 홈의 강점·약점 유형칩. curriculumKey 를 빼면 전체 교육과정에서 고른다 */
export const getStrengthsAndWeaknesses = async (
  studentId: Student['id'],
  curriculumKey?: GetStrengthsAndWeaknessesConceptChipByStudentIdAndCurriculumKeyParams['curriculumKey'],
  meta?: RequestMeta,
): Promise<StrengthsAndWeaknesses> => {
  const body =
    await httpClient().get<GetStrengthsAndWeaknessesConceptChipByStudentIdAndCurriculumKeyData>(
      `/student-history/achievement/student/${studentId}/strengths-and-weaknesses-chips`,
      { params: { curriculumKey }, meta },
    )
  return adaptStrengthsAndWeaknesses(body.data)
}

export type WeeklyLearningSummaryParams = Pick<
  GetCurriculumAnalysisProblemWeeklySummaryByStudentParams,
  'startDate' | 'endDate'
>

/** 기간(yyyy-MM-dd)의 주별 풀이 수·정답률. 학생·학부모 홈의 4주 차트가 쓴다 */
export const getWeeklyLearningSummaries = async (
  studentId: Student['id'],
  params: WeeklyLearningSummaryParams,
  meta?: RequestMeta,
): Promise<WeeklyLearningSummary[]> => {
  const body = await httpClient().get<GetCurriculumAnalysisProblemWeeklySummaryByStudentData>(
    `/student-history/analysis/summary/weekly/${studentId}`,
    { params, meta },
  )
  return adaptWeeklyLearningSummaries(body.data) ?? []
}

export type ParentRecentStudiesParams = GetMaterialViewByStudentId1Params

/** 학부모 홈의 자녀 학습 카드. 기간 안의 학습지·교재·숙제·경시대회를 한 목록으로 받는다 */
export const getParentRecentStudies = async (
  params: ParentRecentStudiesParams,
  signal?: AbortSignal,
  meta?: RequestMeta,
): Promise<RecentStudyItem[]> => {
  const body = await httpClient().get<GetMaterialViewByStudentId1Data>(
    '/parent-home/materialized-view',
    { params, signal, meta },
  )
  return adaptRecentStudyItems(body.data) ?? []
}
